import React, { useState, useEffect, useRef } from 'react';
import { 
  Check, Shield, ShieldOff, Briefcase, 
  Award, MoreHorizontal, Plus, X, ArrowUp
} from 'lucide-react';
import Drawer from '../ui/Drawer';
import { apiFetch } from '../../services/apiClient';
import Button from '../ui/Button';
import SegmentedControl from '../ui/SegmentedControl';
import Switch from '../ui/Switch';
import { INSTITUTIONS, institutionOf, isInstitutionOnly, rotationStartOf, rotationEndOf } from '../../services/staffFields';
import { NO_TEAM_LABEL, teamOf } from '../../services/teams';
import { rankOf, gradeOf } from '../../services/ranks';
import RankGradeFields from './RankGradeFields';
import { fetchPositions, positionOf } from '../../services/positions';

const StaffDrawer = ({ 
  isOpen, 
  onClose, 
  employee, 
  allSkills, 
  onUpdateEmployee,
  isProcessing: externalProcessing,
  teams = [],                       // [{key, label, members}] (services/teams.js)
  staff = [],                       // everybody, for the shadowing picker
}) => {
  const [activeTab, setActiveTab] = useState('general');
  const [localName, setLocalName] = useState('');
  const [localPosition, setLocalPosition] = useState('');   // a position key; '' = none
  const [localRank, setLocalRank] = useState(4);
  const [localGrade, setLocalGrade] = useState(null);
  const [localInstitution, setLocalInstitution] = useState('');
  const [institutionOnly, setInstitutionOnly] = useState(false);
  const [localTeam, setLocalTeam] = useState('');     // a team's key; '' = none
  const [rotationStart, setRotationStart] = useState('');
  const [rotationEnd, setRotationEnd] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [preferredLocations, setPreferredLocations] = useState([]);
  const [isFloater, setIsFloater] = useState(false);
  const [isLocum, setIsLocum] = useState(false);
  const [isOnCallEligible, setIsOnCallEligible] = useState(true);
  const [shadows, setShadows] = useState([]);         // staff ids, priority order
  const [isProcessing, setIsProcessing] = useState(false);
  const lastEmpRef = useRef(null);

  useEffect(() => {
    if (isOpen && employee) {
      setLocalName(employee.name || '');
      setLocalPosition(positionOf(employee));
      // Robust mapping for both snake_case and camelCase
      setLocalRank(rankOf(employee));
      setLocalGrade(gradeOf(employee));
      setLocalInstitution(institutionOf(employee));
      setInstitutionOnly(isInstitutionOnly(employee));
      setLocalTeam(teamOf(teams, employee)?.key || '');
      setRotationStart(rotationStartOf(employee) || '');
      setRotationEnd(rotationEndOf(employee) || '');
      setSelectedSkills(employee.skills || []);
      setPreferredLocations(employee.preferred_locations || employee.preferredLocations || []);
      setIsFloater(employee.is_floater || employee.isFloater || false);
      setIsLocum(employee.is_locum || employee.isLocum || false);
      setShadows(employee.shadows || []);
      setIsOnCallEligible(employee.is_on_call_eligible !== undefined ? employee.is_on_call_eligible : (employee.isOnCallEligible !== undefined ? employee.isOnCallEligible : true));
      
      if (lastEmpRef.current !== (employee.id || employee.name)) {
        setActiveTab('general');
        lastEmpRef.current = employee.id || employee.name;
      }
    } else if (!isOpen) {
      lastEmpRef.current = null;
    }
  }, [isOpen, employee, teams]);

  const rotationInvalid = Boolean(rotationStart && rotationEnd && rotationEnd < rotationStart);

  const handleSave = async () => {
    if (rotationInvalid) return;
    setIsProcessing(true);
    const empData = {
      id: employee.id,              // who this is; `name` may be a rename
      name: localName,
      position: localPosition || '',
      // For the screen's own copy only (the server names the position itself).
      title: positions.find(p => p.key === localPosition)?.label || '',
      // Set on the person (Oct 2026); '' clears the grade (no on-call).
      rank: localRank,
      oncallGrade: localGrade === null ? '' : localGrade,
      // The institution is one of the cluster's four. The team is a staff
      // group's key, and choosing one moves the person out of any other;
      // '' = no team (Unassigned).
      institution: localInstitution || '',
      institutionOnly,
      team: localTeam || '',
      // '' clears a date; the backend refuses an end before the start.
      activeStartDate: rotationStart || '',
      activeEndDate: rotationEnd || '',
      skills: selectedSkills,
      preferredLocations: preferredLocations,
      isFloater: isFloater,
      isLocum: isLocum,
      isOnCallEligible: isOnCallEligible,
      // Sent only when changed, so saving anything else leaves it alone.
      ...(JSON.stringify(shadows) !== JSON.stringify(employee.shadows || []) ? { shadows } : {}),
    };
    await onUpdateEmployee(empData);
    setIsProcessing(false);
    onClose();
  };

  // The department's positions, and the display names for the rank and grade
  // numbers (Ranks & Positions).
  const [positions, setPositions] = useState([]);
  useEffect(() => {
    if (isOpen) fetchPositions().then(setPositions).catch(() => {});
  }, [isOpen]);
  const [rankLabels, setRankLabels] = useState({});
  const [gradeLabels, setGradeLabels] = useState({});
  useEffect(() => {
    apiFetch('/api/rank-maps')
      .then(r => r.json())
      .then(d => {
        setRankLabels(d.clinicalRankLabels || d.clinical_rank_labels || {});
        setGradeLabels(d.oncallRankLabels || d.oncall_rank_labels || {});
      })
      .catch(() => {});
  }, []);

  const renderGeneral = () => (
    <div className="space-y-8 animate-in fade-in duration-300">
      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Full Name</label>
        <div className="relative group">
          <input 
            type="text" 
            value={localName} 
            onChange={(e) => setLocalName(e.target.value)}
            className="w-full h-[44px] px-4 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] transition-all shadow-sm"
            placeholder="Enter employee name..."
          />
        </div>
      </div>

      <RankGradeFields position={localPosition} setPosition={setLocalPosition} positions={positions} rank={localRank} setRank={setLocalRank}
        grade={localGrade} setGrade={setLocalGrade} rankLabels={rankLabels} gradeLabels={gradeLabels} />

      <div className="grid grid-cols-2 gap-6">
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Institution</label>
          <SegmentedControl
            value={localInstitution}
            onChange={setLocalInstitution}
            options={INSTITUTIONS.map(i => ({ id: i, label: i }))}
            className="h-[44px]"
          />
          {localInstitution && (
            <label className="flex items-center gap-2 ml-1 mt-1 text-xs font-semibold text-slate-600 cursor-pointer">
              <input type="checkbox" checked={institutionOnly} onChange={(e) => setInstitutionOnly(e.target.checked)}
                className="accent-[var(--primary)]" />
              Only works at {localInstitution} locations
            </label>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Team</label>
          <select value={localTeam} onChange={(e) => setLocalTeam(e.target.value)}
            className="w-full h-[44px] px-4 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] cursor-pointer">
            <option value="">{NO_TEAM_LABEL}</option>
            {teams.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>

        {/* Rotation / posting dates: outside them the person is not rostered. */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Rotation dates (optional)</label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[9px] font-bold text-slate-400 uppercase ml-1">Starts</span>
              <input type="date" value={rotationStart} onChange={(e) => setRotationStart(e.target.value)}
                className="h-[44px] px-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-[var(--primary)]" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[9px] font-bold text-slate-400 uppercase ml-1">Ends</span>
              <input type="date" value={rotationEnd} onChange={(e) => setRotationEnd(e.target.value)}
                className="h-[44px] px-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:border-[var(--primary)]" />
            </label>
          </div>
          {rotationInvalid
            ? <span className="text-[10px] font-bold text-rose-600 ml-1">The rotation ends before it starts.</span>
            : <span className="text-[10px] text-slate-400 ml-1">Leave blank if the person is always rosterable.</span>}
        </div>
      </div>

      {/* Whom this person shadows, in priority order (the mentor_shadowing
          rule, where a department enables it). */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Shadows</label>
        {shadows.length > 0 && (
          <ol className="flex flex-col gap-1.5">
            {shadows.map((sid, i) => (
              <li key={sid} className="flex items-center gap-2 px-3 h-[36px] bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-700">
                <span className="text-slate-400 w-4">{i + 1}.</span>
                <span className="flex-1 truncate">{staff.find(p => p.id === sid)?.name || sid}</span>
                {i > 0 && (
                  <button type="button" aria-label="Move up" className="p-1 text-slate-400 hover:text-slate-700"
                    onClick={() => setShadows(prev => { const n = [...prev]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; })}>
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                )}
                <button type="button" aria-label="Remove" className="p-1 text-slate-400 hover:text-rose-600"
                  onClick={() => setShadows(prev => prev.filter(x => x !== sid))}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ol>
        )}
        <select value="" onChange={(e) => e.target.value && setShadows(prev => [...prev, e.target.value])}
          className="w-full h-[44px] px-4 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)] cursor-pointer">
          <option value="">{shadows.length ? 'Add another…' : 'Nobody — add someone…'}</option>
          {staff.filter(p => p.id && p.id !== employee?.id && !shadows.includes(p.id))
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      <div className="space-y-4">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Staff Status</label>
        <div className="grid grid-cols-1 gap-3">
          <div className={`flex items-center justify-between p-4 rounded-xl border transition-all ${isFloater ? 'bg-[#042f2e] text-white border-transparent' : 'bg-white border-slate-100 text-slate-600'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isFloater ? 'bg-white/10 text-white' : 'bg-slate-50 text-slate-400'}`}><MoreHorizontal className="w-4 h-4" /></div>
              <div>
                <p className="text-xs font-bold leading-tight">Floater Status</p>
                <p className={`text-[10px] font-medium ${isFloater ? 'text-white/60' : 'text-slate-400'}`}>Flexible allocation across all clinics</p>
              </div>
            </div>
            <Switch isOn={isFloater} onClick={() => setIsFloater(!isFloater)} activeTrack="bg-emerald-500" />
          </div>

          <div className={`flex items-center justify-between p-4 rounded-xl border transition-all ${isLocum ? 'bg-[#042f2e] text-white border-transparent' : 'bg-white border-slate-100 text-slate-600'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isLocum ? 'bg-white/10 text-white' : 'bg-slate-50 text-slate-400'}`}><Briefcase className="w-4 h-4" /></div>
              <div>
                <p className="text-xs font-bold leading-tight">Locum Staff</p>
                <p className={`text-[10px] font-medium ${isLocum ? 'text-white/60' : 'text-slate-400'}`}>Only works preferred on-call dates</p>
              </div>
            </div>
            <Switch isOn={isLocum} onClick={() => setIsLocum(!isLocum)} activeTrack="bg-emerald-500" />
          </div>

          <div className={`flex items-center justify-between p-4 rounded-xl border transition-all ${isOnCallEligible ? 'bg-[#042f2e] text-white border-transparent' : 'bg-slate-50 border-slate-200 opacity-60'}`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isOnCallEligible ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-400'}`}>
                {isOnCallEligible ? <Shield className="w-4 h-4" /> : <ShieldOff className="w-4 h-4" />}
              </div>
              <div>
                <p className="text-xs font-bold leading-tight">On-Call Eligible</p>
                <p className={`text-[10px] font-medium ${isOnCallEligible ? 'text-white/60' : 'text-slate-400'}`}>Eligible for night and weekend duties</p>
              </div>
            </div>
            <Switch isOn={isOnCallEligible} onClick={() => setIsOnCallEligible(!isOnCallEligible)} activeTrack="bg-emerald-500" />
          </div>
        </div>
      </div>
    </div>
  );

  const renderSkills = () => (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <Award className="w-3 h-3 text-slate-400" />
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">Qualifications</label>
        </div>
        <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">{selectedSkills.length} Attained</span>
      </div>
      
      <div className="grid grid-cols-1 gap-2">
        {allSkills.map(skill => {
          const isSelected = selectedSkills.includes(skill);
          return (
            <button 
              key={skill} 
              onClick={() => setSelectedSkills(prev => isSelected ? prev.filter(s => s !== skill) : [...prev, skill])}
              className={`w-full text-left px-4 py-3 rounded-xl border transition-all flex items-center justify-between group
                ${isSelected ? 'bg-[#042f2e] text-white border-transparent shadow-md' : 'bg-white border-slate-100 text-slate-600 hover:border-slate-300'}
              `}
            >
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full ${isSelected ? 'bg-teal-400 animate-pulse' : 'bg-slate-200'}`} />
                <span className="text-xs font-bold">{skill.replace(/^skill_/i, '')}</span>
              </div>
              {isSelected ? <Check className="w-4 h-4" /> : <Plus className="w-4 h-4 opacity-0 group-hover:opacity-100 text-slate-300" />}
            </button>
          );
        })}
        {allSkills.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">No skills in library</p>
          </div>
        )}
      </div>
    </div>
  );

  const footer = (
    <div className="flex justify-end gap-3">
      <Button variant="secondary" onClick={onClose} className="px-6 h-[36px] text-xs font-bold">Cancel</Button>
      <Button 
        variant="primary" 
        onClick={handleSave}
        disabled={rotationInvalid}
        isLoading={isProcessing || externalProcessing}
        className="px-8 h-[36px] text-xs font-bold"
        icon={Check}
      >
        Save Changes
      </Button>
    </div>
  );

  return (
    <Drawer 
      isOpen={isOpen} 
      onClose={onClose} 
      title={employee?.name || 'Staff Profile'}
      footer={footer}
    >
      <div className="flex flex-col h-full">
        <SegmentedControl 
          className="mb-8"
          options={[
            { id: 'general', label: 'General' },
            { id: 'skills', label: 'Qualifications' }
          ]}
          value={activeTab}
          onChange={setActiveTab}
        />
        
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {activeTab === 'general' && renderGeneral()}
          {activeTab === 'skills' && renderSkills()}
        </div>
      </div>
    </Drawer>
  );
};

export default StaffDrawer;
